import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  History, Loader2, UserPlus, ShieldCheck, XCircle, CheckCircle2,
  MessageSquare, FileUp, FileMinus, Download,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  invited: { icon: UserPlus, color: '#3A6EA5', verb: (l) => `invited ${l.detail}` },
  changed_role: { icon: ShieldCheck, color: '#3A6EA5', verb: (l) => `changed a role — ${l.detail}` },
  revoked_share: { icon: XCircle, color: '#C0492E', verb: (l) => `revoked access for ${l.detail}` },
  updated_item: { icon: CheckCircle2, color: '#7FA98C', verb: (l) => `updated "${l.item_title}" (${l.detail})` },
  added_comment: { icon: MessageSquare, color: '#E9B949', verb: (l) => `commented on "${l.item_title}"` },
  added_document: { icon: FileUp, color: '#7FA98C', verb: (l) => `added a file to "${l.item_title}"` },
  deleted_document: { icon: FileMinus, color: '#C0492E', verb: (l) => `removed a file from "${l.item_title}"` },
  exported: { icon: Download, color: '#3A6EA5', verb: () => `exported an executor packet` },
};

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function ActivityLog({ open, onClose, owner }) {
  const token = localStorage.getItem('session_token');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/readiness/audit`, {
      headers: { Authorization: `Bearer ${token}` },
      params: owner ? { owner } : undefined,
    }).then((r) => setLogs(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [open, owner]); // eslint-disable-line

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg rounded-3xl bg-white" data-testid="readiness-activity-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2C3A47] flex items-center gap-2">
            <History className="h-5 w-5 text-[#4F86C6]" /> Activity log
          </DialogTitle>
          <DialogDescription className="sr-only">A record of recent changes to this checklist.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4F86C6]" /></div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-[#9C948B] text-center py-8">No activity yet.</p>
          ) : (
            <ol className="space-y-1">
              {logs.map((l) => {
                const m = ACTION_META[l.action] || { icon: History, color: '#8D8880', verb: () => l.action };
                const Icon = m.icon;
                return (
                  <li key={l.log_id} className="flex items-start gap-3 py-2.5 border-b border-[#F2F1EE] last:border-0">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${m.color}1A` }}>
                      <Icon className="h-4 w-4" style={{ color: m.color }} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#2C3A47]"><span className="font-semibold">{l.actor_name}</span> {m.verb(l)}</p>
                      <p className="text-xs text-[#9C948B]">{timeAgo(l.timestamp)}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
